/* eslint-disable @reatom/unit-naming-rule */
import { Result } from '@blade/std-lib'
import { fetchTradesRequestAction } from '@root/entities/trade/atoms'
import { reatomResource } from '@root/shared/lib'

import type { Atom } from '@reatom/framework'
import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'
import type { Trade } from '@root/entities/trade'

/**
 * A page of the account's own trades.
 *
 * The app has a trades table with sorting, cursors and a realtime tail, and it
 * is the right thing on a pair's page. Here the question is narrower — what did
 * I just do, across all my wallets — so this is the one read behind it, asked
 * with every wallet address as the maker. Fifty rows: a trader reading their
 * own history wants this morning, and a month belongs in an export.
 */
const PAGE = 50

export const reatomPortfolioHistory = ({
  addressesAtom,
  network,
}: {
  addressesAtom: Atom<ReadonlyArray<Address>>
  network: Network.Network
}) =>
  reatomResource(
    // Not an async body: the spy below has to be seen by the reaction that
    // runs this, and an async function hands control back before it is.
    (ctx) => {
      const addresses = ctx.spy(addressesAtom)

      if (addresses.length === 0) {
        // No wallets is a true empty answer, not a request that fails.
        return Promise.resolve(Result.succeed<ReadonlyArray<Trade.Trade>>([]))
      }

      return ctx.schedule(async () => {
        const result = await fetchTradesRequestAction(ctx, {
          network,
          maker: [...addresses],
          sort: 'desc',
          limit: PAGE,
        })

        return Result.map(
          result,
          (page): ReadonlyArray<Trade.Trade> => page.data,
        )
      })
    },
    { name: 'reatomPortfolioHistory', loadOnConnect: true },
  )
